import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OfferModalComponent } from '../offer-modal/offer-modal.component';
import { Campaign, DrawingEventStatus, KnownReward } from '../../models';
import {
  AuthService,
  LogService,
  OfferService,
  StyleService,
  TemplateService,
} from '../../services';
import { Observable, of, take } from 'rxjs';
import { FilterFieldType, MainTemplate, TemplateSetting } from '../../models/template';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../snackbar/snackbar.component';
import { InteractionType } from '../../models/interaction';
import { ChooseRewardItem } from '../../models/chooseReward';
import { DataPointService } from '../../services/data-point.service';
import { LogSource, LogType } from '../../models/common';

@Component({
  selector: 'app-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
})
export class CardComponent implements OnInit {
  @Input() public indexes: [any, any] = [] as any;
  @Input() public tab!: TemplateSetting;
  @Input() public accessRequestId: string = '';
  @Input() public lobbyToken: string = '';
  @Input() public campaign!: Campaign;
  @Input() public displayRewardImageAfterReveal!: boolean;

  @Output() onDetailClose: EventEmitter<any> = new EventEmitter();
  @Output() onDeleteExpiredCampaign: EventEmitter<string> = new EventEmitter();

  public isLoading: boolean = false;
  public mainTemplate!: MainTemplate;
  public drawingEventStatus = DrawingEventStatus;
  public isLiveEarnPeriod: boolean = false;
  public chooseRewardsList: ChooseRewardItem[] = [];

  public thresholdPassed: boolean = false;
  public displayProgressionStatus: boolean = false;
  public dataPoint: string | string[] = "points";
  public pointsEarnedToday: number = 0;
  public pointsThreshold: number = 0;
  public shouldGreyscale: boolean = false;

  private isInteraction: InteractionType = InteractionType.None;

  constructor(
    private readonly dialog: MatDialog,
    private readonly offerService: OfferService,
    private readonly templateService: TemplateService,
    private readonly logService: LogService,
    private readonly datapointService: DataPointService,
    public readonly styleService: StyleService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.checkCampaignThreshold();

    this.templateService
      .templateData$()
      .pipe(take(1))
      .subscribe((template) => {
        this.isLoading = false;
        this.mainTemplate = template;
        this.isLiveEarnPeriod = this.offerService.isLiveEarnPeriod(
          this.campaign
        );
        this.isInteraction = this.offerService.gameInteractionType(
          this.campaign
        );
      });
  }

  public get isModalOpened() {
    return this.templateService.isModalOpened;
  }

  private findEarnSource() {
    return Object.keys(this.campaign.customProperties).find((key) => key.endsWith("EarnSource")) ?? "earnSource";
  }

  private getEarnSourceName(dp: string) {
    return {
      "CreditsFromSlot": "a_TierPointsEarnedFromSlot",
      "CreditsFromDining": "a_TierPointsEarnedFromHotel",
      "CreditsFromRetail": "a_TierPointsEarnedFromRetail",
      "CreditsFromTable": "a_TierPointsEarnedFromTable",
    }[dp];
  }

  private checkCampaignThreshold() {
    if (!!this.campaign.customProperties?.dataPoint) {
      this.displayProgressionStatus = this.campaign.customProperties.displayProgressionStatus;

      if (Array.isArray(this.campaign.customProperties.dataPoint)) {
        this.dataPoint = this.campaign.customProperties.dataPoint;
      } else {
        this.dataPoint = FilterFieldType[this.campaign.customProperties.dataPoint as keyof typeof FilterFieldType];
      }

      if (this.dataPoint === FilterFieldType.PointsEarnedToday) {
        this.pointsEarnedToday = parseInt(this.datapointService.playerActivity.a_PointsEarned);
        this.pointsThreshold = this.campaign.customProperties.pointsEarnedToday;
      } else if (this.dataPoint === FilterFieldType.TierPointsEarnedToday) {
        this.pointsEarnedToday = parseInt(this.datapointService.playerActivity.a_TierPointsEarned);
        this.pointsThreshold = this.campaign.customProperties.tierPointsEarnedToday;
      } else if (this.dataPoint === FilterFieldType.EarnPeriodPoints) {
        const key: any = this.datapointService.allDataPoints.find(
          (val) => val.name === this.campaign.customProperties[this.findEarnSource()]
        )?.id as string;

        this.pointsEarnedToday = parseFloat(
          this.datapointService.getValueByDataPointProperty(
            key,
            this.campaign.id
          )
        );

        this.pointsEarnedToday = isNaN(this.pointsEarnedToday) ? 0 : this.pointsEarnedToday;
        this.pointsThreshold = this.campaign.customProperties.earnPointsThreshold;
      } else if (
        this.dataPoint === FilterFieldType.CreditsFromDining ||
        this.dataPoint === FilterFieldType.CreditsFromRetail ||
        this.dataPoint === FilterFieldType.CreditsFromSlot ||
        this.dataPoint === FilterFieldType.CreditsFromTable
      ) {
        this.pointsEarnedToday = this.datapointService.playerActivity[this.campaign.id]?.earnPeriod?.[`${this.getEarnSourceName(this.campaign.customProperties.dataPoint)}`];
        this.pointsEarnedToday = isNaN(this.pointsEarnedToday) ? 0 : this.pointsEarnedToday;
        this.pointsThreshold = this.campaign.customProperties.earnPointsThreshold;
      } else if (Array.isArray(this.dataPoint)) {
        // summation 
        let totalPoints = 0;

        (this.dataPoint as string[]).forEach((datapoint: string) => {
          totalPoints += parseFloat(
            (this.datapointService.playerActivity[this.campaign.id]?.earnPeriod?.[`${this.getEarnSourceName(datapoint)}`] ?? "0")
          );
        });

        this.pointsEarnedToday = isNaN(totalPoints) ? 0 : totalPoints;
        this.pointsThreshold = this.campaign.customProperties.totalPoints;
      }

      if (this.pointsEarnedToday < this.pointsThreshold) {
        this.thresholdPassed = false;
        this.shouldGreyscale = true;
      } else {
        this.thresholdPassed = true;
      }

      if (!!this.campaign.customProperties?.shortFormText) {
        this.dataPoint = this.campaign.customProperties.shortFormText;
      }
    } else {
      this.thresholdPassed = true;
    }
  }

  public openDetail(): void {
    this.templateService.isModalOpened = true;

    if (!this.campaign.knownReward) {
      if (this.isCampaignExpired()) {
        this.snackBar.openFromComponent(SnackbarComponent, {
          panelClass: 'error',
          duration: 5000,
          horizontalPosition: 'end',
          data: {
            type: 'error',
            message: 'Campaign has been expired',
          },
        });
        this.templateService.isModalOpened = false;
        this.onDeleteExpiredCampaign.emit(this.campaign.id);
        return;
      }
    }

    let request: Observable<KnownReward> = of(this.campaign.knownReward);
    let isFirstRequest = this.campaign.knownReward === null;
    let dialogRef: MatDialogRef<any, any>;

    if (isFirstRequest) {
      if (this.campaign.isOpening) {
        this.templateService.isModalOpened = false;
        return;
      };
      this.campaign.isOpening = true;
    }

    if (
      this.isInteraction != InteractionType.GameInteraction &&
      isFirstRequest
    ) {
      request = this.offerService.getCamapign(
        this.campaign.id,
        this.accessRequestId,
        this.campaign.schedule.schedulerId
      );
    }

    request.pipe(take(1)).subscribe({
      next: async (rewardData) => {
        this.offerService.setDefaultCustomProperties(rewardData);
        this.campaign.knownReward = rewardData;

        const dialogData = {
          indexesOffer: this.indexes,
          tab: this.tab,
          offer: this.campaign,
          accessRequestId: this.accessRequestId,
          lobbyToken: this.lobbyToken,
          reward: rewardData,
        };

        const isChooseOffer = this.isInteraction === InteractionType.RewardInteraction &&
          rewardData.stepType === 'ChooseReward' &&
          !rewardData.reward;

        if (this.isInteraction != InteractionType.RewardInteraction) {
          dialogRef = this.dialog.open(OfferModalComponent, {
            data: {
              ...dialogData,
              isNeedToReedemOrUpdateBalance:
                this.isInteraction === InteractionType.None && isFirstRequest,
            },
          });
        } else if (isChooseOffer) {
          if (rewardData.instanceId) {
            const list = await this.offerService.getChooseRewards(rewardData.instanceId).toPromise();
            this.chooseRewardsList = list?.items as ChooseRewardItem[];
          }
          
          dialogRef = this.dialog.open(OfferModalComponent, {
            data: {
              ...dialogData,
              chooseRewardsList: this.chooseRewardsList,
            },
          });
        }

        dialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe((needToUpdate) => {
            this.templateService.isModalOpened = false;
            this.campaign.isOpening = false;
            if (needToUpdate) {
              this.logService.writeLog(`offer received from modal: ${needToUpdate}, afterClosed taken place`, LogType.DEBUG, LogSource.MODAL)
              this.onDetailClose.emit();
            }
          });
      },
      error: (err) => {
        this.logService.writeLog(
          JSON.stringify(`
            Open offer details: Campaign Id - ${this.campaign.id}
            Error:${JSON.stringify(err)}`
          ),
          LogType.ERROR,
          LogSource.OFFER
        );
      },
    });
  }

  private isCampaignExpired(): boolean {
    return this.campaign.schedule.items.some((item) => {
      return new Date() >= new Date(item.expirationDate);
    });
  }
}